import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import LoadingSpinner from '../UI/LoadingSpinner'
import { X, User, Mail, Shield, Package, Edit2 } from 'lucide-react'
import { usersAPI } from '../../services/usersAPI'
import api from '../../services/api'
import { productsAPI } from '../../services/productsAPI'
import toast from 'react-hot-toast'

export default function EditUserModal({ isOpen, onClose, user, organizationId, onSuccess }) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState([])
  const [productRoles, setProductRoles] = useState({}) // { productId: [roles] }
  const [userProductRoles, setUserProductRoles] = useState({}) // { productId: roleId }
  const queryClient = useQueryClient()

  const { register, handleSubmit, reset, formState: { errors }, setValue, watch } = useForm()

  // Fetch organization subscriptions
  const { data: subscriptionsData, isLoading: subscriptionsLoading } = useQuery(
    ['subscriptions', organizationId],
    () => api.get(`/subscriptions/organization/${organizationId}`).then(r => r.data),
    { enabled: isOpen && !!organizationId }
  )
  const subscriptions = subscriptionsData?.data?.subscriptions || []

  // Fetch product roles for a specific product
  const fetchProductRoles = async (productId) => {
    if (!productId) return

    try {
      const rolesResponse = await productsAPI.getRoles(productId)
      const roles = rolesResponse.data?.data?.roles || []
      setProductRoles(prev => ({
        ...prev,
        [productId]: roles
      }))
    } catch (error) {
      console.error('Error fetching product roles:', error)
      setProductRoles(prev => ({
        ...prev,
        [productId]: []
      }))
    }
  }

  // Fetch user's current product roles
  useEffect(() => {
    if (isOpen && user?.id) {
      const fetchUserProductRoles = async () => {
        try {
          const userResponse = await usersAPI.getById(user.id)
          const productRoles = userResponse.data?.data?.product_roles || []
          const rolesMap = {}
          productRoles.forEach(pr => {
            if (pr.product_id) {
              rolesMap[pr.product_id] = pr.role_id || ''
            }
          })
          setUserProductRoles(rolesMap)
        } catch (error) {
          console.error('Error fetching user product roles:', error)
        }
      }
      fetchUserProductRoles()
    }
  }, [isOpen, user?.id])

  // Pre-fetch product roles for all subscribed products
  useEffect(() => {
    if (subscriptions.length > 0 && isOpen) {
      subscriptions.forEach(subscription => {
        if (subscription.product_id && !productRoles[subscription.product_id]) {
          fetchProductRoles(subscription.product_id)
        }
      })
    }
  }, [subscriptions, isOpen])

  // Initialize form and selected subscriptions when modal opens
  useEffect(() => {
    if (isOpen && user) {
      // Initialize form with user data
      reset({
        full_name: user.full_name || '',
        email: user.email || '',
        status: user.status || 'active'
      })

      // Initialize selected subscription IDs from user's subscription_ids
      let userSubscriptionIds = user.subscription_ids || []
      if (typeof userSubscriptionIds === 'string') {
        try {
          userSubscriptionIds = JSON.parse(userSubscriptionIds)
        } catch (e) {
          userSubscriptionIds = []
        }
      }
      if (!Array.isArray(userSubscriptionIds)) {
        userSubscriptionIds = []
      }
      setSelectedSubscriptionIds(userSubscriptionIds)
    }
  }, [isOpen, user, reset])

  // Get current subscribed products (based on selectedSubscriptionIds)
  const getCurrentSubscribedProducts = () => {
    if (!subscriptions.length) return []
    return subscriptions.filter(sub => selectedSubscriptionIds.includes(sub.id))
  }

  // Get available products (not yet selected)
  const getAvailableProducts = () => {
    return subscriptions.filter(sub => !selectedSubscriptionIds.includes(sub.id))
  }

  const updateUserMutation = useMutation(
    (userData) => usersAPI.update(user.id, userData),
    {
      onSuccess: () => {
        toast.success('User updated successfully!')
        queryClient.invalidateQueries(['users', organizationId])
        queryClient.refetchQueries(['users', organizationId])
        queryClient.invalidateQueries(['acceptedInvitations', organizationId])
        onSuccess && onSuccess()
        handleClose()
      },
      onError: (error) => {
        console.error('Error updating user:', error)
        toast.error(error.response?.data?.message || 'Failed to update user')
      }
    }
  )

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    try {
      // Prepare update data with subscription_ids and product roles
      const formattedProductRoles = {}
      Object.entries(userProductRoles).forEach(([productId, roleId]) => {
        const role = (productRoles[productId] || []).find(
          (r) => String(r.id) === String(roleId)
        )
        formattedProductRoles[productId] = {
          role_id: roleId || '',
          role_name: role?.name || role?.title || ''
        }
      })

      const updateData = {
        full_name: data.full_name,
        email: data.email,
        status: data.status,
        subscription_ids: selectedSubscriptionIds,
        product_roles: formattedProductRoles
      }

      await updateUserMutation.mutateAsync(updateData)
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      setSelectedSubscriptionIds([])
      setUserProductRoles({})
      setProductRoles({})
      onClose()
    }
  }

  const handleSubscriptionToggle = (subscriptionId) => {
    setSelectedSubscriptionIds(prev => {
      if (prev.includes(subscriptionId)) {
        // Remove product role when unsubscribing
        const subscription = subscriptions.find(s => s.id === subscriptionId)
        if (subscription?.product_id) {
          setUserProductRoles(prev => {
            const updated = { ...prev }
            delete updated[subscription.product_id]
            return updated
          })
        }
        return prev.filter(id => id !== subscriptionId)
      } else {
        // Fetch product roles when subscribing
        const subscription = subscriptions.find(s => s.id === subscriptionId)
        if (subscription?.product_id && !productRoles[subscription.product_id]) {
          fetchProductRoles(subscription.product_id)
        }
        return [...prev, subscriptionId]
      }
    })
  }

  const handleProductRoleChange = (productId, roleId) => {
    setUserProductRoles(prev => ({
      ...prev,
      [productId]: roleId || ''
    }))
  }

  if (!isOpen) return null

  const currentSubscribedProducts = getCurrentSubscribedProducts()
  console.log('currentSubscribedProducts', currentSubscribedProducts)
  const availableProducts = getAvailableProducts()

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <User className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Edit User</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* User Info - Two Column Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <Input
                  id="full_name"
                  type="text"
                  {...register('full_name', { 
                    required: 'Full name is required',
                    minLength: {
                      value: 2,
                      message: 'Full name must be at least 2 characters'
                    }
                  })}
                  className="w-full"
                  placeholder="Enter full name"
                  error={errors.full_name?.message}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  {...register('email', { 
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                  className="w-full"
                  placeholder="Enter email address"
                  error={errors.email?.message}
                  disabled={isSubmitting}
                  readOnly
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  id="status"
                  {...register('status', { required: 'Status is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-100 cursor-not-allowed"
                  disabled
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>
            </div>

            {/* Subscribed Products Section */}
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center space-x-2 mb-4">
                <Package className="h-5 w-5 text-gray-600" />
                <h3 className="text-lg font-semibold text-gray-900">Product Subscriptions</h3>
              </div>

              {subscriptionsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <LoadingSpinner size="sm" />
                </div>
              ) : (
                <>
                  {/* Current Subscribed Products */}
                  {currentSubscribedProducts.length > 0 && (
                    <div className="mb-6">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Currently Subscribed Products</h4>
                      <div className="space-y-3">
                        {currentSubscribedProducts.map((subscription) => {
                          const productId = subscription.product_id
                          const availableRoles = productRoles[productId] || []
                          const currentRoleId = userProductRoles[productId] || ''
                          
                          return (
                            <div
                              key={subscription.id}
                              className="space-y-2"
                            >
                              <p className="text-base font-bold text-gray-900">
                                {subscription.product_name || 'Unknown Product'}
                              </p>
                               {productId && (
                                 <div>
                                   <label htmlFor={`product-role-${subscription.id}`} className="block text-xs font-medium text-gray-700 mb-1">
                                     Role
                                   </label>
                                  <select
                                    id={`product-role-${subscription.id}`}
                                    value={currentRoleId}
                                    onChange={(e) => handleProductRoleChange(productId, e.target.value)}
                                    className="w-full px-3 py-2 text-sm border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                                    disabled={isSubmitting}
                                  >
                                    <option value="">No role assigned</option>
                                    {availableRoles.map((role) => (
                                      <option key={role.id} value={role.id}>
                                        {role.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Available Products to Subscribe */}
                  {availableProducts.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Available Products</h4>
                      <div className="space-y-3">
                        {availableProducts.map((subscription) => {
                          const productId = subscription.product_id
                          const isSelected = selectedSubscriptionIds.includes(subscription.id)
                          const availableRoles = productRoles[productId] || []
                          const currentRoleId = userProductRoles[productId] || ''
                          
                          return (
                            <div
                              key={subscription.id}
                              className="p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-start space-x-3 mb-2">
                                <input
                                  type="checkbox"
                                  id={`subscription-${subscription.id}`}
                                  checked={isSelected}
                                  onChange={() => handleSubscriptionToggle(subscription.id)}
                                  disabled={isSubmitting}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                                />
                                <div className="flex-1">
                                  <label
                                    htmlFor={`subscription-${subscription.id}`}
                                    className="text-sm font-medium text-gray-900 cursor-pointer block"
                                  >
                                    {subscription.product_name || 'Unknown Product'}
                                  </label>
                                  {subscription.product_description && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {subscription.product_description}
                                    </p>
                                  )}
                                </div>
                              </div>
                              {isSelected && productId && (
                                <div className="mt-3 ml-7 p-2 bg-blue-50 border border-blue-200 rounded-md">
                                  <label htmlFor={`available-product-role-${subscription.id}`} className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-2">
                                    <Shield className="h-3.5 w-3.5 text-blue-600" />
                                    <span>Product Role</span>
                                  </label>
                                  <select
                                    id={`available-product-role-${subscription.id}`}
                                    value={currentRoleId}
                                    onChange={(e) => handleProductRoleChange(productId, e.target.value)}
                                    className="w-full px-3 py-2 text-sm border-2 border-blue-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer hover:border-blue-400 transition-colors"
                                    disabled={isSubmitting}
                                  >
                                    <option value="">No role assigned</option>
                                    {availableRoles.map((role) => (
                                      <option key={role.id} value={role.id}>
                                        {role.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {currentSubscribedProducts.length === 0 && availableProducts.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p>No products available for subscription</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="min-w-[120px]"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

